const incomeGroupStateKey = "incomeManagementExpandedGroups";

const getDefaultIncomeGroupState = () => ({ years: {}, months: {} });

const readIncomeGroupState = () => {
    if (typeof window === "undefined") return null;
    try {
        const raw = sessionStorage.getItem(incomeGroupStateKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return {
            years: parsed?.years && typeof parsed.years === "object" ? parsed.years : {},
            months: parsed?.months && typeof parsed.months === "object" ? parsed.months : {},
        };
    } catch {
        return null;
    }
};

const buildMonthKey = (year, monthKey) => `${year}-${monthKey}`;

const resolveIncomeDate = (income) => income?.invoiceDate || income?.uploadDate;

const resolveIncomeMonthInfo = (income) => {
    const dateValue = resolveIncomeDate(income);
    if (!dateValue) return null;
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return null;
    const year = parsed.getFullYear();
    const month = parsed.getMonth();
    return { year, month, yearKey: String(year), monthKey: String(month) };
};

const sortIncomesByDate = (items) => {
    if (!Array.isArray(items)) return [];
    return [...items].sort((a, b) => {
        const dateA = resolveIncomeDate(a) || "";
        const dateB = resolveIncomeDate(b) || "";
        return dateB.localeCompare(dateA);
    });
};

const normalizeIncomeMonths = (payload) => {
    if (!Array.isArray(payload)) return [];
    const normalized = [];
    payload.forEach((entry) => {
        const year = Number(entry?.year);
        if (!Number.isFinite(year)) return;
        const months = Array.isArray(entry?.months)
            ? entry.months
            : entry?.month !== undefined
            ? [entry.month]
            : [];
        months.forEach((monthValue) => {
            const month = Number(monthValue);
            if (!Number.isFinite(month)) return;
            const monthIndex = month - 1;
            if (monthIndex < 0 || monthIndex > 11) return;
            normalized.push({ year, month: monthIndex });
        });
    });
    return normalized;
};

const pad2 = (n) => {
    const value = Number(n);
    return value < 10 ? `0${value}` : String(value);
};

const toDateInputValue = (value) => {
    if (!value) return "";
    if (value instanceof Date && !Number.isNaN(value)) {
        return value.toISOString().slice(0, 10);
    }
    const s = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})[T\s]/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const m = s.match(/^(\d{4})[,/-](\d{1,2})[,/-](\d{1,2})$/);
    if (m) {
        const [, y, mo, d] = m;
        return `${y}-${pad2(mo)}-${pad2(d)}`;
    }
    const d = new Date(s);
    if (!Number.isNaN(d)) {
        return d.toISOString().slice(0, 10);
    }
    return "";
};

const recurrenceOptions = [
    { value: "MONTHLY", label: "Monatlich" },
    { value: "QUARTERLY", label: "Quartalsweise" },
    { value: "YEARLY", label: "Jährlich" },
];

const recurrenceLabel = (value) => {
    switch (value) {
        case "YEARLY":
            return "Jährlich";
        case "QUARTERLY":
            return "Quartalsweise";
        case "MONTHLY":
        default:
            return "Monatlich";
    }
};

const parseDecimalInput = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    if (normalized === "") return null;
    const sanitized = normalized.replace(",", ".");
    const parsed = Number(sanitized);
    return Number.isFinite(parsed) ? parsed : null;
};

const roundCurrency = (value) =>
    Number.isFinite(value) ? Math.round(value * 100) / 100 : value;

const resolveEuNetAmount = (data) => {
    if (!data) return null;
    const net = parseDecimalInput(data.netAmount);
    if (Number.isFinite(net)) {
        return net;
    }
    const gross = parseDecimalInput(data.grossAmount);
    if (!Number.isFinite(gross)) {
        return null;
    }
    const vat = parseDecimalInput(data.vatAmount);
    if (Number.isFinite(vat)) {
        return roundCurrency(gross - vat);
    }
    return roundCurrency(gross);
};

const applyEuCalculation = (current, patch, changedField, formatAmount) => {
    const next = { ...(current || {}), ...(patch || {}) };
    if (!next.isEuService) {
        return next;
    }
    const gross = parseDecimalInput(next.grossAmount);
    const net = parseDecimalInput(next.netAmount);
    const vat = parseDecimalInput(next.vatAmount);
    const computeNet = (grossValue) =>
        roundCurrency(Number.isFinite(vat) ? grossValue - vat : grossValue);
    const computeGross = (netValue) =>
        roundCurrency(Number.isFinite(vat) ? netValue + vat : netValue);

    if (changedField === "grossAmount" && Number.isFinite(gross)) {
        return { ...next, netAmount: formatAmount(computeNet(gross)) };
    }
    if (changedField === "netAmount" && Number.isFinite(net)) {
        return { ...next, grossAmount: formatAmount(computeGross(net)) };
    }
    if (changedField === "vatAmount" || changedField === "isEuService") {
        if (Number.isFinite(gross)) {
            return { ...next, netAmount: formatAmount(computeNet(gross)) };
        }
        if (Number.isFinite(net)) {
            return { ...next, grossAmount: formatAmount(computeGross(net)) };
        }
    }
    return next;
};

const toEditableString = (value) => (value === null || value === undefined ? "" : String(value));

const parseDayOfMonth = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return null;
    const normalized = Math.trunc(parsed);
    if (Number.isNaN(normalized)) return null;
    if (normalized < 1) return 1;
    if (normalized > 31) return 31;
    return normalized;
};

const extOf = (filename) => {
    const s = String(filename ?? "");
    const i = s.lastIndexOf(".");
    return i > -1 ? s.slice(i + 1).toUpperCase() : "DATEI";
};

const formatDate = (d) => {
    try {
        return new Date(d).toLocaleDateString("de-DE", { year: "numeric", month: "2-digit", day: "2-digit" });
    } catch {
        return "—";
    }
};

const formatDateOrDash = (d) => {
    if (!d) return "—";
    return formatDate(d);
};

const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const fmtEUR = (v) =>
    new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(v) || 0);

const emptyToNull = (v) => (v === "" || v === undefined ? null : v);

const parseOrNull = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

const isFiniteNumber = (v) => typeof v === "number" && Number.isFinite(v);

const toNumOrEmpty = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : "";
};

export {
    applyEuCalculation,
    buildMonthKey,
    emptyToNull,
    extOf,
    formatBytes,
    formatDate,
    formatDateOrDash,
    fmtEUR,
    getDefaultIncomeGroupState,
    incomeGroupStateKey,
    isFiniteNumber,
    normalizeIncomeMonths,
    pad2,
    parseDayOfMonth,
    parseDecimalInput,
    parseOrNull,
    readIncomeGroupState,
    recurrenceLabel,
    recurrenceOptions,
    resolveEuNetAmount,
    resolveIncomeDate,
    resolveIncomeMonthInfo,
    roundCurrency,
    sortIncomesByDate,
    toDateInputValue,
    toEditableString,
    toNumOrEmpty,
};
